import { inject, Injectable } from '@angular/core';
import { LenderService } from '@shared/service/lender/lender.service';
import { PROTOCOL_MORTGAGE } from '../../product-helper';
import { JointCreditManager } from '../../types/joint-applicant/joint-credit-manager.class';
import { getIterationId, getPageIterationId, processPath } from '../utilities.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import {
  ADDITIONAL_BENEFICIAL_COUNT,
  OAO_MEMBERSHIP_TYPE,
  RESPONSIBLE_PARTIES,
  RESPONSIBLE_PARTIES_ISPARTY
} from '@workflow/ecp-shared/constant/constants';
import { IdentificationType, IncomeFrequency, OaoMembershipTypes } from '@workflow/ecp-shared/enum/enum';
import { ValueLabelItem } from '@workflow/class/enum-item.class';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { WorkflowDictionaryService } from '@workflow/service/workflow-dictionary/workflow-dictionary.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { AlloyService } from '../alloy/alloy.service';
import { StatefulService } from '@app/service/stateful.service';
import { FieldService } from '@workflow/class/field.service';

/**
 * Methods on this service get registered as 'logic functions' on LogicService
 */
@Injectable({
  providedIn: 'root'
})
export class VisibleFunctionsService {
  private readonly statefulService = inject(StatefulService);
  private readonly fieldService = inject(FieldService);

  constructor(
    public readonly dictionaryService: WorkflowDictionaryService,
    private readonly lenderService: LenderService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly workflowSvc: WorkflowService,
    private readonly alloySvc: AlloyService
  ) {}

  /**
   * Returns true if testDate value is not yet over the requiredYears in the past
   * @param context logic fn field context
   * @param path source value storage path
   * @param requiredYears number of years required
   */
  public missingTimeCoverage = (context: LogicFnContext, path: string, requiredYears: number): boolean => {
    const MIN_YEAR = 1900;
    const N_DAYS = 31;
    const N_MONTHS = 12;

    const passDate = (date: string): boolean => {
      const startDate = new Date(date);
      return Date.now() >= startDate.setFullYear(startDate.getFullYear() + +requiredYears);
    };

    const iterationId = this.statefulService.arraySize(path, context.field.pageIterationId) - 1;
    const parts = this.statefulService.wrap(path, iterationId, context.field.pageIterationId).valueStringOnly.split('/');
    const month = +parts.shift();
    if (parts.length === 1) {
      const year = +parts.shift();
      if (month > 0 && month <= N_MONTHS && year >= MIN_YEAR && year <= new Date().getFullYear()) {
        return !passDate(`${month}/01/${year}`);
      }
    } else {
      const day = +parts.shift();
      const year = +parts.shift();
      if (month > 0 && month <= N_MONTHS && day > 0 && day <= N_DAYS && year >= MIN_YEAR && year <= new Date().getFullYear()) {
        return !passDate(`${month}/${day}/${year}`);
      }
    }
    return false;
  };

  public showOnFirstCard = (context: LogicFnContext): boolean => {
    const fieldOrCard = context.field || context.card;
    return fieldOrCard.iterationId === 0;
  };

  public dontShowOnFirstCard = (context: LogicFnContext): boolean => {
    return !this.showOnFirstCard(context);
  };

  /**
   * Check if card is last for field
   * @param context field context
   * @param path path on storage
   */
  public showOnLastCard = (context: LogicFnContext, pathValue: string): boolean => {
    const path = processPath(pathValue, context.field.pageIterationId);
    return context.field.iterationId === this.statefulService.arraySize(path) - 1;
  };

  /**
   * Check if card is last manually added
   * @param context field context
   * @param sourcePath path to source type
   * @param automatedSource value of source type for automated
   */
  public showOnLastManualCard = (context: LogicFnContext, sourcePath: string, automatedSource: string, selectedField?: string): boolean => {
    let lastManualIndex = -1;
    const path = sourcePath.split('/$/')[0];
    const pageIterationId = context.field.pageIterationId;
    const len = this.statefulService.arraySize(path, pageIterationId);
    for (let i = 0; i < len; i++) {
      const val =
        this.statefulService.wrap(sourcePath, i, pageIterationId).valueString &&
        this.statefulService.wrap(sourcePath, i, pageIterationId).valueString.toLowerCase();
      if (automatedSource.toLowerCase() !== val) {
        lastManualIndex = i;
      }
    }

    if (lastManualIndex === -1) {
      return context.field.iterationId === len - 1;
    }
    const isLastAdd =
      lastManualIndex === context.field.iterationId &&
      selectedField &&
      !!this.statefulService.wrap(selectedField, context.field.iterationId, pageIterationId).value;

    return isLastAdd;
  };

  /**
   * Returns true if referenced array is empty
   * @param context field context
   * @param path path on storage
   */
  public isEmptyArray = (context: LogicFnContext, path: string): boolean => {
    return !this.statefulService.arraySize(processPath(path, context));
  };

  /**
   * Returns true if referenced array is not empty
   * @param context field context
   * @param path path on storage
   */
  public isNotEmptyArray = (context: LogicFnContext, path: string): boolean => {
    return !this.isEmptyArray(context, path);
  };

  /**
   * Returns true if referenced array size is less than given number
   * @param context field context
   * @param path path on storage
   */
  public isArraySizeLessThan = (context: LogicFnContext, path: string, size: number): boolean => {
    return this.statefulService.arraySize(path) < size;
  };

  /**
   * check if any elements of the array has a value equal to the filter value
   * @param context field context
   * @param path array storage path
   * @param filterValue the expected value
   */
  public hasAny = (context: LogicFnContext, arrayPath: string, filterValue: string): boolean => {
    const len = this.statefulService.arraySize(arrayPath);
    if (len === 0) {
      return false;
    }

    for (let i = 0; i < len; i++) {
      const val = this.statefulService.wrap(`${arrayPath}`, i).value;
      if (val === filterValue) {
        return true;
      }
    }
    return false;
  };

  /**
   * check if non of the elements in the array has a value equal to the filter value
   */
  public hasNotAny = (context: LogicFnContext, arrayPath: string, filterValue: string): boolean => {
    return !this.hasAny(context, arrayPath, filterValue);
  };

  /**
   * Is True(false if passed in as conditional) or not first card
   * @param context field context
   * @param fieldId Field to copy from
   * @param conditional should field be true or false
   */
  public isTrueOrNotFirstCard = (context: LogicFnContext, fieldId: string, conditional = true): boolean => {
    const field = this.fieldService.createField(fieldId, context.field.flowId, context.field.iterationId, context.field.pageIterationId);
    return (field.data && field.data.value === conditional) || context.field.iterationId > 0;
  };

  /**
   * Hide field if backing storage is falsy
   * Returns true only if there's value
   * @param context field context
   */
  public hideIfEmpty = (context: LogicFnContext): boolean => {
    return (
      context.field.config.storageId &&
      !!this.statefulService.wrap(context.field.config.storageId, context.field.iterationId, context.field.pageIterationId).value
    );
  };

  // TODO: This function is not used anywhere in the codebase. Should we remove it?
  public showEmploymentHistory = (context: LogicFnContext): boolean => {
    return this.isYouthMembership(context) ? false : true;
  };

  // TODO: This function is not used anywhere in the codebase. Should we remove it?
  public membershipIsType = (context: LogicFnContext, evaluatedMembershipType, membershipTypeFieldId): boolean => {
    const membershipField = this.fieldService.createField(membershipTypeFieldId, context.field.flowId);
    const selectedMembership = membershipField.enum.filter((field) => field.value === membershipField.data.value)[0];

    return selectedMembership ? selectedMembership.enumId.toLowerCase() === evaluatedMembershipType.toLowerCase() : false;
  };

  /**
   * Show Identity verification page.
   * @param context - LogicFnContext
   */

  public showIdentityVerificationPage = (
    context: LogicFnContext,
    lenderSettingStorageId: string,
    preciseIdCompletionFlagStorageId: string,
    IsPrimaryApplicantVisited: boolean
  ): boolean => {
    const oaolenderSettings = this.statefulService.wrap(lenderSettingStorageId).value;
    const PIDCompleted = this.statefulService.wrap(preciseIdCompletionFlagStorageId).value;
    const enabledAdminToolPID = this.isEnabledPreciseId();
    // Checking page visitedor not only if mid app decision disabled
    const isPageNotVisited = this.isMidApplicationDecisionEnabled(context) ? true : !IsPrimaryApplicantVisited;
    // if currentPage is identity then make page visible true
    const isCurrentPageIdentity = this.workflowSvc.currentPage && this.workflowSvc.currentPage.id === context.page.id;

    return oaolenderSettings && !PIDCompleted && enabledAdminToolPID && (isPageNotVisited || isCurrentPageIdentity);
  };

  private isEnabledPreciseId = (): boolean => {
    const selectedMembershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
    const adminPortalFeatures = this.adminPortalFeaturesService?.config;
    const adminMembership = adminPortalFeatures?.oaoConfiguration?.memberships?.filter((t) => t.membershipID === selectedMembershipId);
    const enableMembershipPreciseId = adminMembership && adminMembership.length > 0 ? adminMembership[0].isEnabledPID : false;
    return adminPortalFeatures?.oaoFeatures?.enablePreciseId && enableMembershipPreciseId;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public showAlloyVerificationPage = (context: LogicFnContext): boolean => {
    // if on app review, block navigation back the context passed in
    // is always the alloy page, so we need to check against the workflow
    if (this.workflowSvc.currentPage?.id.includes('review')) {
      return false;
    }
    const alloyState = this.alloySvc.isAlloyEnabled();
    // we don't care about state here - handle that in the guard
    return alloyState.enabled;
  };

  public showCoApplicantPage = (context: LogicFnContext, coAppCount: number): boolean => {
    const selectedMembershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
    const adminPortalFeatures = this.adminPortalFeaturesService?.config;
    const adminMembership = adminPortalFeatures?.oaoConfiguration?.memberships?.filter((t) => t.membershipID === selectedMembershipId);

    if (adminMembership && adminMembership.length > 0) {
      return adminMembership[0]?.canAllowCoApplicant && adminMembership[0]?.allowedNumberOfCoApplicant >= coAppCount;
    }

    return false;
  };

  /**
   * Show ApplicationCheck point page.
   * @param context - LogicFnContext
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isMidApplicationDecisionEnabled = (context: LogicFnContext): boolean => {
    const bundleValue = this.statefulService.wrap('api:oaoMemberInformation/isMidAppDecisionEnabled').value;
    return typeof bundleValue == 'boolean' ? bundleValue : false;
  };

  /**
   * Check the given storageId address contains the PO Box. If so return true, else false.
   * @param context - LogicFnContext
   * @param storageId - string
   */
  public checkPOBoxAddress = (context: LogicFnContext, storageId: string): boolean => {
    // get the pobox display message from dictionary service & replace with the msg holder

    // get the iterationid to get te address text.
    const iterationID = context.field.iterationId;
    const address = this.statefulService.wrap(storageId, iterationID, context.field.pageIterationId).value;
    const poBoxRegex = /\b(?:p[\W_]*o[\W_]*box|post[\W_]*office[\W_]*box|p[\W_]*o[\W_]*b)\d*\b/i;
    // \b: Word boundary to ensure it matches whole words.
    // (?:...): Non-capturing group for alternatives.
    // [\W_]*: Matches any number of non-word characters or underscores (e.g., spaces, dashes, dots).
    // \d*: Matches any number of digits (e.g., box numbers).
    // /i: Case-insensitive flag.
    // Matches: PO Box 123, P.O. Box 456, Post Office Box 789, P O Box 1011, P_O_Box_1213,
    // P-O-B 1415, p.o.box, p o b, post_office_box, P.O.B123, p-o box, p_o box

    if (address) {
      return poBoxRegex.test(String(address));
    }
    return false;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public showAutoFillOption = (context: LogicFnContext): boolean => {
    const autoFillStatus = this.statefulService.wrap(`api:oaoMemberInformation/primaryApplicant/autoFillStatus`).value;
    const autoFillOption = this.statefulService.wrap(`api:oaoMemberInformation/primaryApplicant/autoFillOption`).value;
    const status = autoFillOption && autoFillOption === 'MobileNumber' && autoFillStatus && autoFillStatus === true ? false : true;
    if (!status) {
      this.statefulService.wrap('mem:primary-application-status').value = true;
    }

    return status;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public showAutoFillOptioncoapplicant = (context: LogicFnContext): boolean => {
    const autoFillStatus = this.statefulService.wrap(`api:oaoMemberInformation/coApplicant/autoFillStatus`).value;
    const autoFillOption = this.statefulService.wrap(`api:oaoMemberInformation/coApplicant/autoFillOption`).value;
    const status = autoFillOption && autoFillOption === 'MobileNumber' && autoFillStatus && autoFillStatus === true ? false : true;
    if (!status) {
      this.statefulService.wrap('mem:primary-application-status-co-applicant').value = true;
    }

    return status;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public showAutoFillOptionaddcoapplicant = (context: LogicFnContext): boolean => {
    const autoFillStatus = this.statefulService.wrap(`api:oaoMemberInformation/additionalCoApplicant/autoFillStatus`).value;
    const autoFillOption = this.statefulService.wrap(`api:oaoMemberInformation/additionalCoApplicant/autoFillOption`).value;
    const status = autoFillOption && autoFillOption === 'MobileNumber' && autoFillStatus && autoFillStatus === true ? false : true;
    if (!status) {
      this.statefulService.wrap('mem:primary-application-status-additional-co-app').value = true;
    }

    return status;
  };

  public hideActionButtonOnInstaTouch = (context: LogicFnContext, storagePath: string, applicationType = 'OAO'): boolean => {
    const isMembership = applicationType.toLowerCase() === 'oao';
    const isInstaTouchEnabled = isMembership
      ? this.visibleIsOAOInstatouchEnabled()
      : this.adminPortalFeaturesService.config?.olaFeatures?.canShowInstatouch;
    const pageId = getPageIterationId(context);
    const iterationId = getIterationId(context);
    const autoFillStatus = this.statefulService.wrap(`${storagePath}/autoFillStatus`, iterationId, pageId).value;
    const autoFillOption = this.statefulService.wrap(`${storagePath}/autoFillOption`, iterationId, pageId).valueString;
    const status = autoFillOption?.toLowerCase() !== 'manual' && autoFillStatus !== true && isInstaTouchEnabled;
    return status;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isYouthMembership = (context: LogicFnContext): boolean => {
    const value = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').valueString;
    return value ? JSON.parse(value) : false;
  };
  public isNotYouthMembership = (context: LogicFnContext): boolean => {
    return !this.isYouthMembership(context);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isBusinessMembership = (context: LogicFnContext): boolean => {
    const value = this.statefulService.wrap(OAO_MEMBERSHIP_TYPE).valueString;
    return value && value.toLowerCase() === OaoMembershipTypes.Business.toLowerCase();
  };
  public isNotBusinessMembership = (context: LogicFnContext): boolean => {
    return !this.isBusinessMembership(context);
  };

  /**
   * Show additional Beneficiary Card for Business.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public showAdditionalBeneficiaryCard = (context: LogicFnContext): boolean => {
    const responsibleCardSize = this.statefulService.arraySize(RESPONSIBLE_PARTIES);
    let selectedYesCount = 0;
    const maxCount = 4;
    for (let i = 0; i < responsibleCardSize; i++) {
      const temp = this.statefulService.wrap(RESPONSIBLE_PARTIES_ISPARTY, i);
      if (temp && temp.value === true) {
        selectedYesCount++;
      }
    }
    return maxCount - selectedYesCount > 0;
  };

  /**
   *  Show Beneficiary Card list for Business
   */
  public showAdditionalBeneficicaryCardList = (context: LogicFnContext): boolean => {
    const isAdditionalSelected = this.statefulService.wrap(ADDITIONAL_BENEFICIAL_COUNT).value;
    return isAdditionalSelected && this.showAdditionalBeneficiaryCard(context);
  };

  /**
   *  Show Instattouch  Card list for Business
   */
  public showInstatouchBusiness = (context: LogicFnContext, autfillOptionStorageID: string, autfillStatusStoragID: string): boolean => {
    const index = context && context.card && context.card.pageIterationId;
    const option = this.statefulService.wrap(autfillOptionStorageID, index).value;
    const status = this.statefulService.wrap(autfillStatusStoragID, index).value;
    return option === 'Manual' || status === true;
  };

  /**
   * @param context context for the current card
   * @param type 'primary' identified as reposnisble parties in 0 position
   * @returns boolean
   */
  public checkForActualEconsentCard = (context: LogicFnContext, type: 'primary' | 'additional') => {
    const index = context && context.card && context.card.pageIterationId;
    if (type === 'primary' && index === 0) {
      return true;
    }
    if (type !== 'primary' && index > 0) {
      return true;
    }
    return false;
  };

  /**
   *  Show/Hide Co-Applicant's Econsent Card for Business memberhip.
   * If Splash screen is enabled, hide econsent card for Primary applicant & show it for all co-applicants
   */
  public showBusinessEconsentCard = (context: LogicFnContext): boolean => {
    const pageIterationId = context && context.card && context.card.pageIterationId;
    const enableConsentScreen = this.adminPortalFeaturesService?.config?.consentManagement?.primaryAcknowledgement?.enableConsentScreen;
    return enableConsentScreen === true ? pageIterationId > 0 : true;
  };

  /**
   * show Id Number Fields
   */
  public showIDNumber = (context: LogicFnContext, storageId: string): boolean => {
    try {
      const idType = this.statefulService.wrap(storageId, context.field.iterationId, context.field.pageIterationId).valueStringOnly;
      // eslint-disable-next-line eqeqeq
      return idType && idType != IdentificationType.Minor.toString() ? true : false;
    } catch (e) {
      return false;
    }
  };

  /**
   * show Issued By Text Field
   */
  public showIssuedBy = (context: LogicFnContext, storageId: string): boolean => {
    try {
      const idType = this.statefulService.wrap(storageId, context.field.iterationId, context.field.pageIterationId).valueStringOnly;
      return idType &&
        (idType.toString().toLowerCase() === IdentificationType.AlumniID.toString() ||
          idType.toString().toLowerCase() === IdentificationType.ForeignDriversLicense.toString() ||
          idType.toString().toLowerCase() === IdentificationType.CertificateofIndianStatus.toString() ||
          idType.toString().toLowerCase() === IdentificationType.EmployeeID.toString() ||
          idType.toString().toLowerCase() === IdentificationType.HealthInsuranceCard.toString() ||
          idType.toString().toLowerCase() === IdentificationType.PermanentResidentCard.toString() ||
          idType.toString().toLowerCase() === IdentificationType.StudentID.toString() ||
          idType.toString().toLowerCase() === IdentificationType.TribalID.toString() ||
          idType.toString().toLowerCase() === IdentificationType.WorkAuthorizationCard.toString() ||
          idType.toString().toLowerCase() === IdentificationType.VeteransAffairVACard.toString() ||
          idType.toString().toLowerCase() === IdentificationType.SocialSecurityBenefitVerificationLetter.toString() ||
          idType.toString().toLowerCase() === IdentificationType.MedicareCard.toString() ||
          idType.toString().toLowerCase() === IdentificationType.CreditDebitCard.toString() ||
          idType.toString().toLowerCase() === IdentificationType.CurrentBankStatement.toString() ||
          idType.toString().toLowerCase() === IdentificationType.CurrentCreditCardStatement.toString() ||
          idType.toString().toLowerCase() === IdentificationType.CurrentLoanStatement.toString() ||
          idType.toString().toLowerCase() === IdentificationType.CurrentPaystub.toString() ||
          idType.toString().toLowerCase() === IdentificationType.CurrentUtilityBill.toString() ||
          idType.toString().toLowerCase() === IdentificationType.MediCalCard.toString() ||
          idType.toString().toLowerCase() === IdentificationType.MedicaidCard.toString() ||
          idType.toString().toLowerCase() === IdentificationType.RealEstateTaxBill.toString() ||
          idType.toString().toLowerCase() === IdentificationType.SocialSecurityCard.toString())
        ? true
        : false;
    } catch (e) {
      return false;
    }
  };

  /**
   * show Issue Date
   */
  public showIssueDate = (context: LogicFnContext, storageId: string): boolean => {
    try {
      const idType = this.statefulService.wrap(storageId, context.field.iterationId, context.field.pageIterationId).valueStringOnly;
      // eslint-disable-next-line eqeqeq
      return idType && idType != IdentificationType.BirthCertificate.toString() && idType != IdentificationType.Minor.toString()
        ? true
        : false;
    } catch (e) {
      return false;
    }
  };

  /**
   * Enable Co APP / Joint App Card Section
   */
  public enableCoApp = (context: LogicFnContext, stateofPageID: string, requiredStorageId: string): boolean => {
    const coAPPLocked = this.statefulService.wrap(stateofPageID).valueStringOnly;
    const required = this.statefulService.wrap(requiredStorageId).valueString;
    return !(coAPPLocked.toLowerCase() === 'coapplocked' && required);
  };

  /**
   * show authorization if a full name has been entered
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public showAuthorizations = (context: LogicFnContext): boolean => {
    const jcm = new JointCreditManager(this.statefulService, PROTOCOL_MORTGAGE);
    // eslint-disable-next-line eqeqeq
    const returnValue = jcm.allApplicants.filter((t) => t.firstName != '' && t.lastName != '').length > 0;
    return returnValue;
  };

  /**
   * show Issued By Text Field
   */
  public showAverageHoursWorked = (context: LogicFnContext, storageId: string): boolean => {
    try {
      const frequencyType = this.statefulService.wrap(storageId, context.field.iterationId, context.field.pageIterationId).valueStringOnly;
      return frequencyType && frequencyType.toString().toLowerCase() === IncomeFrequency.Hourly.toString().toLowerCase() ? true : false;
    } catch (e) {
      return false;
    }
  };

  public showAddAnotherEmployment = (context: LogicFnContext, sourcePath: string): boolean => {
    const employmentSource = this.statefulService.wrap(
      sourcePath,
      context.field.iterationId,
      context.field.pageIterationId
    ).valueStringOnly;
    if (
      employmentSource &&
      employmentSource.toLowerCase() === 'voi' &&
      this.lenderService.lenderData &&
      this.lenderService.lenderData.isRequiredEmploymentHistory
    ) {
      return false;
    }
    return true;
  };

  /**
   *  Show PersonalInfoCard card based on autofill for Personal Membership
   */

  public showPersonalInfoCard = (context: LogicFnContext, autfillOptionStorageID: string, autfillStatusStoragID: string): boolean => {
    const autoFillStatus = this.statefulService.wrap(autfillStatusStoragID).value;
    const autoFillOption = this.statefulService.wrap(autfillOptionStorageID).value;
    return (
      !this.visibleIsOAOInstatouchEnabled() ||
      autoFillOption === 'Manual' ||
      ((autoFillOption === 'MobileNumber' || autoFillOption === 'Prefill') && autoFillStatus === true)
    );
  };

  /**
   *  Show IdentificationTypeChildFields based on lender && Membership
   */

  public showIdentificationTypeChildFields = (
    context: LogicFnContext,
    fieldId: string,
    idType: string,
    identificationFieldsTypeConfig: string
  ): boolean => {
    if (!idType) {
      return false;
    }

    if (!identificationFieldsTypeConfig) {
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const objIdentificationTypeChildFields: any = JSON.parse(identificationFieldsTypeConfig);

    return objIdentificationTypeChildFields && objIdentificationTypeChildFields[fieldId]
      ? objIdentificationTypeChildFields[fieldId].isRequired
      : false;
  };

  /**
   *  Hide or Show Promotion code text box
   */

  public showOAOPromotionCode = (): boolean => {
    const isVisible = this.statefulService.wrap('mem:oaoMemberInformation/membership/isPromoCode').value as boolean;
    return this.adminPortalFeaturesService?.config?.oaoFeatures?.allowPromoCode && isVisible;
  };

  public visibleIsOAOInstatouchEnabled = (): boolean => {
    const adminPortalFeatures = this.adminPortalFeaturesService?.config;
    return adminPortalFeatures && this.lenderService.lenderDataInfo
      ? adminPortalFeatures?.oaoFeatures?.enableInstatouch
      : this.lenderService.lenderDataInfo?.isOAOInstatouchEnabled;
  };

  public enableEnhancedProductSelection = (): boolean => {
    return this.lenderService.lenderData.enableEnhancedProductSelection;
  };

  // TODO: is there a way in workflow to set visible to ["enableEnhancedProductSelection", false] to avoid duplicate code like this?
  public disableEnhancedProductSelection = (): boolean => {
    return !this.enableEnhancedProductSelection();
  };

  // Used to hide back button if previous page is membership or loan selection
  // We do not want the user to change their product selection after they have already selected one
  public previousPageIsProductSelection = (context: LogicFnContext): boolean => {
    return context?.page?.previousPage?.id === 'oao-membership' || context?.page?.previousPage?.id === 'ola-consumer-loan-type';
  };

  // Used to show or hide back button based on eligibility
  public previousPageIsEligibility = (context: LogicFnContext): boolean => {
    const isOlaSkipEligibility = this.statefulService.wrap('ola:skipEligibility').value as boolean;
    return (
      context?.page?.previousPage?.id === 'oao-eligibility' ||
      (context?.page?.previousPage?.id === 'ola-eligibility' && isOlaSkipEligibility)
    );
  };

  //  Hide Eligibility Questions Based on Admin Portal features for membership.
  public isOAOEligibilityQuestionsEnabled = (): boolean => {
    return this.adminPortalFeaturesService?.config?.oaoFeatures?.canShowEligibilityQuestions;
  };

  /**
   * Show Mobile phone number as default field for contact info
   * @param context logic fn field context
   * @param applicantType applicant type path
   * @param protocol storage prefix
   */
  public showOAODefaultContactInfo = (context: LogicFnContext, applicantType: string = 'primary', protocol: string = 'api'): void => {
    const pageId = getPageIterationId(context);
    const iterationID = getIterationId(context);
    const contactInfo = this.statefulService.wrap(`${protocol}:oaoMemberInformation/${applicantType}/phoneType`, iterationID, pageId).value;
    if (!contactInfo) {
      this.statefulService.wrap(`${protocol}:oaoMemberInformation/${applicantType}/phoneType`, iterationID, pageId).value = 'Mobile';
    }
  };

  /**
   * Show OAO Resident Monthyl Payment Field
   */
  public showOAOResidentMontlyPayment = (context: LogicFnContext, fieldId: string): boolean => {
    const field = this.fieldService.createField(fieldId, context.field?.flowId, context.field?.iterationId, context.field?.pageIterationId);
    if (field.config.payload) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/dot-notation
      if (field.config.payload['dropdownLists'] && (field.config.payload['dropdownLists'] as any).length > 0) {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const dropdownValues = field.config.payload['dropdownLists']
          ? // eslint-disable-next-line @typescript-eslint/dot-notation
            (JSON.parse(field.config.payload['dropdownLists'] as string) as ValueLabelItem[])
          : [];
        if (field.data.value && dropdownValues) {
          const items = dropdownValues.filter((f) => f.value === field.data.value);
          const canShow = items && items[0].label.toLowerCase() !== 'owns free and clear';
          if (!canShow) {
            context.field.data.value = null;
          }
          return canShow;
        }
        return true;
      }
    }
    return false;
  };
}
